import { editDigits, type DigitEdit, type DigitEditResult } from "./digit-input.ts"

/**
 * 일본 전화번호 표기 — 숫자를 치면 하이픈이 따라 붙는다(`080-1234-1234`).
 *
 * 저장도 하이픈이 붙은 모양 그대로다(지금 들어 있는 값들이 이미 그 모양이라 옮길 것이 없다).
 * 상세 화면·CSV 는 저장된 값을 그대로 보여 주므로 따로 고칠 것이 없다.
 *
 * ## 어디서 끊는가 (총무성 전기통신번호제도)
 *
 * 고정전화는 `0` + 시외국번(1~4자리) + 시내국번(1~4자리) + 가입자번호(4자리) = 10자리이고,
 * **시외+시내가 합쳐서 5자리**다. 그 5자리를 어디서 끊는지는 지역마다 달라 총무성 고시 표
 * (약 5천 건)를 봐야 정확히 알 수 있다 — 규칙으로는 정해지지 않는다.
 *
 * 그래서 확실한 것만 규칙으로 두고, 나머지 고정전화는 가장 흔한 3-3-4 로 끊는다:
 *
 * | 앞자리 | 끊는 자리 | 보기 |
 * |---|---|---|
 * | 070·080·090·050 | 3-4-4 | `080-1234-1234` (지금 쓰이는 값은 전부 이 꼴) |
 * | 0120·0570 | 4-3-3 | `0120-123-456` |
 * | 0800 | 4-3-4 | `0800-123-4567` |
 * | 03·06 (도쿄·오사카) | 2-4-4 | `03-1234-5678` |
 * | 그 밖의 0으로 시작 | 3-3-4 | `045-123-4567` |
 *
 * ⚠️ 지방의 **4자리 시외국번**(`0154-23-4567`)은 이 규칙으로 `015-423-4567` 로 잘못 끊긴다.
 * 자리 수는 맞으므로 번호 자체가 틀어지지는 않는다. 정확히 끊으려면 고시 표를 들여와야 한다.
 *
 * 제도상 휴대폰은 3-3-5 지만, 표기 관행은 3-4-4 이고 이 시스템의 기존 값도 그 모양이다.
 */

/** 어떤 나라 번호든 숫자는 15자리를 넘지 않는다(E.164). 넘겨 받은 숫자를 잃지 않기 위한 상한. */
export const PHONE_MAX_DIGITS = 15

const PATTERNS: Array<{ prefix: RegExp; groups: number[] }> = [
  { prefix: /^0120/, groups: [4, 3, 3] },
  { prefix: /^0570/, groups: [4, 3, 3] },
  { prefix: /^0800/, groups: [4, 3, 4] },
  // 휴대폰(070·080·090)과 IP전화(050). 0800 을 먼저 봐야 한다 — 여기에도 걸린다.
  { prefix: /^0[5789]0/, groups: [3, 4, 4] },
  { prefix: /^0[36]/, groups: [2, 4, 4] },
  { prefix: /^0/, groups: [3, 3, 4] },
]

function patternOf(digits: string): number[] | null {
  return PATTERNS.find((entry) => entry.prefix.test(digits))?.groups ?? null
}

const sum = (groups: number[]) => groups.reduce((total, size) => total + size, 0)

/**
 * 입력칸의 글에서 숫자만 뽑는다.
 *
 * 붙여넣기로 들어오는 국제표기(`+81 80-1234-1234`)는 일본 국내표기(`080…`)로 바꾼다.
 */
export function phoneDigits(raw: string): string {
  const international = /^\s*[+＋]\s*81/.test(raw)
  const digits = raw.replace(/\D/g, "")
  const national = international ? `0${digits.replace(/^81/, "")}` : digits
  return national.slice(0, PHONE_MAX_DIGITS)
}

/**
 * 보여 주는 모양. 입력 중(자릿수가 덜 찼을 때)에도 쓴다.
 *
 * 일본 번호로 보이지 않으면(0으로 시작하지 않거나, 그 앞자리의 자릿수를 넘으면)
 * **숫자를 그대로 둔다** — 한국 번호처럼 다른 나라 번호를 적었을 때 숫자를 잃지 않게.
 */
export function formatPhoneDigits(digits: string): string {
  const groups = patternOf(digits)
  if (groups === null || digits.length > sum(groups)) return digits

  const parts: string[] = []
  let rest = digits
  for (const size of groups) {
    if (rest === "") break
    parts.push(rest.slice(0, size))
    rest = rest.slice(size)
  }
  return parts.join("-")
}

/**
 * 이미 저장된 값을 보여 줄 때. 숫자·하이픈·공백 말고 다른 글자가 섞여 있으면
 * (내선 안내 같은 것) 손대지 않고 원문 그대로 둔다.
 */
export function formatPhone(raw: string | null | undefined): string | null {
  if (raw === null || raw === undefined || raw === "") return null
  if (/[^0-9\s+＋-]/.test(raw)) return raw
  return formatPhoneDigits(phoneDigits(raw))
}

export type PhoneEdit = DigitEdit
export type PhoneEditResult = DigitEditResult

/** 입력칸 한 번의 편집을 숫자와 보이는 모양으로 옮긴다. 저장에는 `formatted` 를 쓴다. */
export function editPhone(edit: PhoneEdit): PhoneEditResult {
  return editDigits(edit, { digitsOf: phoneDigits, format: formatPhoneDigits })
}
